/**
 * RISK MODAL COMPONENT
 * 
 * Full-featured modal for adding/editing risks
 * Includes category selection, impact levels, contingency tracking
 * 
 * Based on Document 5 specification
 */

import React, { useState, useMemo } from 'react';
import { X, AlertTriangle, DollarSign, Clock, Users, Info } from 'lucide-react';
import { RISK_CATEGORIES, IMPACT_LEVELS, RISK_OWNERS } from '../../constants/riskCategories';

// Helper to get initial form data
const getInitialFormData = (risk) => ({
  category: risk?.category || '',
  description: risk?.description || '',
  impact_level: risk?.impact_level || 'medium',
  cost_impact_estimate: risk?.cost_impact_estimate || '',
  time_impact_estimate: risk?.time_impact_estimate || '',
  mitigation_plan: risk?.mitigation_plan || '',
  risk_owner: risk?.risk_owner || 'contractor',
  area_id: risk?.area_id || '',
  area_label: risk?.area_label || '',
  contingency_included: risk?.contingency_included || false,
  contingency_amount: risk?.contingency_amount || '',
});

const RiskModal = ({
  risk = null,  // If provided, editing mode
  areas = [],
  onSave,
  onClose,
}) => {
  const isEditing = !!risk;
  
  // Initialize form with risk data (using function to compute initial state)
  const [formData, setFormData] = useState(() => getInitialFormData(risk));
  
  const [errors, setErrors] = useState({});

  const selectedCategory = RISK_CATEGORIES.find(c => c.value === formData.category);

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    // Clear error when field is updated
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const handleAreaChange = (areaId) => {
    const area = areas.find(a => a.area_id === areaId);
    setFormData(prev => ({
      ...prev,
      area_id: areaId,
      area_label: area?.label || '',
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.category) {
      newErrors.category = 'Risk category is required';
    }
    
    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = 'Please provide a detailed description (at least 10 characters)';
    }
    
    if (formData.contingency_included && !formData.contingency_amount) {
      newErrors.contingency_amount = 'Please enter contingency amount';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    
    const riskData = {
      ...formData,
      contingency_amount: formData.contingency_included 
        ? parseFloat(formData.contingency_amount) || 0 
        : null,
    };
    
    onSave(riskData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {isEditing ? 'Edit Risk' : 'Add Risk'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Risk Category <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {RISK_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => updateField('category', cat.value)}
                  className={`
                    p-3 flex flex-col items-center gap-1 border rounded transition-all
                    ${formData.category === cat.value
                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                      : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                    }
                  `}
                >
                  <span className="text-2xl">{cat.icon}</span>
                  <span className="text-xs text-center leading-tight">{cat.label}</span>
                </button>
              ))}
            </div>
            {errors.category && (
              <p className="mt-1 text-sm text-red-400">{errors.category}</p>
            )}
          </div>

          {/* Category Examples */}
          {selectedCategory && selectedCategory.examples.length > 0 && (
            <div className="p-3 bg-slate-800/50 border border-slate-700 rounded">
              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Common examples for {selectedCategory.label}:
              </p>
              <ul className="text-xs text-slate-500 space-y-1">
                {selectedCategory.examples.slice(0, 4).map((example, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-slate-600">•</span>
                    {example}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Description <span className="text-red-400">*</span>
              <span className="text-slate-500 font-normal ml-2">(Be specific about THIS risk)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={4}
              className={`
                w-full px-4 py-3 bg-slate-950 border text-slate-100 rounded
                focus:outline-none resize-none
                ${errors.description ? 'border-red-500' : 'border-slate-700 focus:border-amber-500'}
              `}
              placeholder="Describe the specific risk in detail..."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-400">{errors.description}</p>
            )}
          </div>

          {/* Impact Level */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Impact Level <span className="text-red-400">*</span>
            </label>
            <div className="space-y-2">
              {IMPACT_LEVELS.map(level => (
                <label
                  key={level.value}
                  className={`
                    flex items-start gap-3 p-3 border rounded cursor-pointer transition-all
                    ${formData.impact_level === level.value
                      ? `border-${level.color === 'green' ? 'emerald' : level.color}-500/50 bg-${level.color === 'green' ? 'emerald' : level.color}-500/10`
                      : 'border-slate-700 hover:border-slate-600'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="impact_level"
                    value={level.value}
                    checked={formData.impact_level === level.value}
                    onChange={(e) => updateField('impact_level', e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <span className={`
                      font-medium
                      ${level.value === 'low' ? 'text-emerald-400' : ''}
                      ${level.value === 'medium' ? 'text-amber-400' : ''}
                      ${level.value === 'high' ? 'text-red-400' : ''}
                    `}>
                      {level.icon} {level.label}
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">{level.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Cost & Time Impact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Estimated Cost Impact
              </label>
              <input
                type="text"
                value={formData.cost_impact_estimate}
                onChange={(e) => updateField('cost_impact_estimate', e.target.value)}
                className="w-full px-4 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded
                  focus:border-amber-500 focus:outline-none"
                placeholder="e.g., $500-2,000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Estimated Time Impact
              </label>
              <input
                type="text"
                value={formData.time_impact_estimate}
                onChange={(e) => updateField('time_impact_estimate', e.target.value)}
                className="w-full px-4 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded
                  focus:border-amber-500 focus:outline-none"
                placeholder="e.g., 1-2 days"
              />
            </div>
          </div>

          {/* Related Area */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Related Area (optional)
            </label>
            <select
              value={formData.area_id}
              onChange={(e) => handleAreaChange(e.target.value)}
              className="w-full px-4 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded
                focus:border-amber-500 focus:outline-none"
            >
              <option value="">Global / Site-wide</option>
              {areas.map(area => (
                <option key={area.area_id} value={area.area_id}>
                  {area.label}
                </option>
              ))}
            </select>
          </div>

          {/* Mitigation Plan */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Mitigation Plan
              <span className="text-slate-500 font-normal ml-2">(How will you handle this?)</span>
            </label>
            <textarea
              value={formData.mitigation_plan}
              onChange={(e) => updateField('mitigation_plan', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-slate-950 border border-slate-700 text-slate-100 rounded
                focus:border-amber-500 focus:outline-none resize-none"
              placeholder="Describe how this risk will be addressed..."
            />
          </div>

          {/* Risk Owner */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Risk Owner <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-3">
              {RISK_OWNERS.map(owner => (
                <label
                  key={owner.value}
                  className={`
                    flex-1 p-3 border rounded cursor-pointer text-center transition-all
                    ${formData.risk_owner === owner.value
                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                      : 'border-slate-700 text-slate-400 hover:border-slate-600'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="risk_owner"
                    value={owner.value}
                    checked={formData.risk_owner === owner.value}
                    onChange={(e) => updateField('risk_owner', e.target.value)}
                    className="sr-only"
                  />
                  <span className="font-medium">{owner.label}</span>
                  <p className="text-xs text-slate-500 mt-1">{owner.description}</p>
                </label>
              ))}
            </div>
          </div>

          {/* Contingency */}
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.contingency_included}
                onChange={(e) => updateField('contingency_included', e.target.checked)}
                className="w-5 h-5 bg-slate-950 border-slate-600 rounded focus:ring-amber-500"
              />
              <span className="text-slate-300 font-medium">Include contingency in estimate</span>
            </label>
            
            {formData.contingency_included && (
              <div>
                <label className="block text-sm text-slate-400 mb-1">
                  Contingency Amount ($)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <input
                    type="number"
                    value={formData.contingency_amount}
                    onChange={(e) => updateField('contingency_amount', e.target.value)}
                    className={`
                      w-full pl-8 pr-4 py-2 bg-slate-950 border text-slate-100 rounded
                      focus:outline-none
                      ${errors.contingency_amount ? 'border-red-500' : 'border-slate-700 focus:border-amber-500'}
                    `}
                    placeholder="1000"
                  />
                </div>
                {errors.contingency_amount && (
                  <p className="mt-1 text-sm text-red-400">{errors.contingency_amount}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-700 text-slate-400 hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors"
          >
            {isEditing ? 'Save Changes' : 'Add Risk'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RiskModal;
